import { getClosestRootNode } from '../../utils/ids-dom-utils/ids-dom-utils';

export type IdsSubTabItemData = {
  id?: string;
  comment?: string;
  disabled?: boolean;
  icon?: string;
  selected?: boolean;
  shortcutKeys?: string;
  submenu?: IdsSubTabData;
  text: string;
  type: 'item';
  value?: string | null;
};

export type IdsSubTabHeaderData = {
  for?: string;
  text: string;
  type: 'header';
};

export type IdsSubTabSeparatorData = {
  type: 'separator';
};

export type IdsSubTabContentsData = Array<IdsSubTabGroupData | IdsSubTabSeparatorData | IdsSubTabHeaderData>;

export type IdsSubTabGroupData = {
  id?: string;
  items: Array<IdsSubTabItemData | IdsSubTabSeparatorData | IdsSubTabHeaderData>;
  select?: 'none' | 'single' | 'multiple';
  type?: 'group';
};

export type IdsSubTabObjectData = {
  id?: string;
  contents?: IdsSubTabContentsData;
  length?: number;
};

export type IdsSubTabData = IdsSubTabObjectData | IdsSubTabContentsData;

// Menu Selection Types
const SUB_TABS_SELECT_TYPES = [
  'none',
  'single',
  'multiple'
];

// Default Button state values
const SUB_TABS_DEFAULTS: any = {
  disabled: false,
  icon: null,
  selected: false,
  shortcutKeys: null,
  submenu: null,
  tabIndex: 0,
  value: null,
};

/**
 * Determines if a menu item's stored value can safely be described by its attribute inside the DOM.
 * @param {any} value the value to be checked
 * @returns {boolean} true if the value can be "stringified" safely for the DOM attribute
 */
function safeForAttribute(value: any) {
  return value !== null && ['string', 'number', 'boolean'].includes(typeof value);
}

/**
 * @private
 * @param {string|HTMLElement} subTabGroup the group to search for
 * @param {HTMLElement} idsSubTab the parent menu element
 * @returns {HTMLElement|undefined} if valid, a reference to the menu group.
 * Otherwise, returns undefined.
 */
function isValidGroup(subTabGroup: any, idsSubTab: any) {
  let hasGroup;

  // eslint-disable-next-line no-undef
  const isGroup = subTabGroup !== undefined && subTabGroup.nodeName === 'IDS-SUB-TABS';
  idsSubTab.groups.forEach((group: any) => {
    if ((isGroup && group.isEqualNode(subTabGroup)) || (group?.id === subTabGroup)) {
      hasGroup = group;
    }
  });
  return hasGroup;
}

/**
 * @private
 * @param {HTMLElement} item the element to be checked
 * @param {HTMLElement} IdsSubTab the parent menu element
 * @returns {boolean} true if the provided element is a "currently-usable" IdsSubTabItem type.
 */
function isUsableItem(item: any, IdsSubTab: any) {
  const isItem = item?.nodeName === 'IDS-SUB-TAB';
  if (!isItem) {
    return false;
  }

  // The item is only usable if it's contained by the correct IdsSubTab
  const menuHasItem = IdsSubTab.contains(item);

  // In some nested cases, we need to detect the item's Shadow Root containment to accurately
  // figure out if it's slotted inside the same menu.
  const closestItemRoot = getClosestRootNode(item.assignedSlot);
  const itemInMenuShadow = closestItemRoot?.menu?.isEqualNode(IdsSubTab);

  return (itemInMenuShadow || menuHasItem) && !item.disabled;
}

export {
  SUB_TABS_SELECT_TYPES, SUB_TABS_DEFAULTS, safeForAttribute, isValidGroup, isUsableItem
};
